import { i18n } from '@appbase/util'
import { DirectiveOptions } from 'vue'
import SynapEditorWrapper from './SynapEditorWrapper'

declare var synapEditorConfig: editor.synap.SynapEditorConfig
declare var SynapEditor: any

var callbacks: { [key: string]: Function } = {}
var loadedEditorIds: string[] = []

/** SynapEditor Editor 초기화된 상태 */
export function loaded(id: string) {
    return loadedEditorIds.includes(id)
}

export function unload(id: string) {
    const idx = loadedEditorIds.indexOf(id)
    if (idx >= 0) {
        loadedEditorIds = [
            ...loadedEditorIds.slice(0, idx),
            ...loadedEditorIds.slice(idx + 1),
        ]
    }
    SynapEditor.destroy(id)
}

const editorDirective: DirectiveOptions = {
    inserted: (el, binding, vnode) => {
        const ctx = vnode.context as SynapEditorWrapper
        const { editorId, initialValue, synapopts: options, mode, onedit } = ctx
        const { rawName, value } = binding as any
        
        Object.assign(synapEditorConfig, options);

        if (rawName === 'v-synap:loaded') {
            callbacks[editorId] = value
        }   
           
        // 읽기 모드인 경우 툴바 기능 제거
        if (mode === 'view') {
            synapEditorConfig['editor.toolbar'] = []
        } else {
            synapEditorConfig['editor.mode.iframe'] = {
                enable: true,
                "script.urls": ['/cm/gres/libs/synap-editor/SynapEditor/iframeMode/SEPolyfill.min.js'],
                "style.urls": ['/cm/gres/libs/synap-editor/SynapEditor/iframeMode/contentsEditStyle.css']
            },
            synapEditorConfig['editor.balloon'] = {
                'tableCell': ['formulaInput', '-', 'fill', 'selectBorder', 'borderColor', 'lineThickness', 'lineStyle', 'fitTableWidth', '-', 'insertRowBefore', 'insertRowAfter', 'insertColBefore', 'insertColAfter', 'deleteRow', 'deleteCol', 'deleteTable', '-', 'contentsAlign', 'verticalAlign',  'cellSize', 'mergeCell', 'splitCell', 'cellProperties', 'tableProperties']
            }
        }

        synapEditorConfig['editor.lang'] =
            i18n.locale === 'cn' ? 'zh' : i18n.locale


        /**
         * 클립보드에서 붙여넣은 HTML에서 위험한 요소를 제거합니다.
         * 주의: 이 함수는 붙여넣을 데이터만 처리하며, 에디터의 기존 내용은 건드리지 않습니다.
         *
         * - <script>, <link>, <base>, <meta> 태그 제거
         * - <style> 태그 제거 (외부 사이트의 전역 스타일이 에디터에 영향 방지)
         * - <iframe>, <object>, <embed> 태그 제거
         * - 인라인 style 속성은 보존 (개별 요소의 서식 유지)
         * - 이벤트 핸들러 속성 제거 (onclick, onerror 등)
         * - javascript: 프로토콜 제거
         */
        function sanitizePastedHtml(html: string): string {
            // DOM 파서 생성
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');

            // 1. 위험한 태그 완전 제거
            // - <script>: JavaScript 코드 실행
            // - <style>: 외부 사이트의 전역 CSS 규칙이 에디터에 영향
            // - <link>: 외부 스타일시트/리소스 로드
            // - <iframe>, <object>, <embed>: 외부 콘텐츠 삽입
            // - <base>: 상대 URL 기준을 외부 사이트로 변경
            // - <meta>: 메타 정보
            const dangerousTags = ['script', 'link', 'style', 'base', 'meta', 'iframe', 'object', 'embed'];
            dangerousTags.forEach(tag => {
                const elements = doc.querySelectorAll(tag);
                elements.forEach(el => el.remove());
            });

            // 2. 이벤트 핸들러 및 위험 속성 제거
            const allElements = doc.querySelectorAll('*');
            allElements.forEach(el => {
                const attrs = Array.from(el.attributes);
                attrs.forEach(attr => {
                    // on으로 시작하는 이벤트 핸들러 제거 (onclick, onerror 등)
                    if (attr.name.startsWith('on')) {
                        el.removeAttribute(attr.name);
                    }
                    // javascript: 프로토콜 제거
                    if (attr.value && typeof attr.value === 'string' &&
                        attr.value.trim().toLowerCase().startsWith('javascript:')) {
                        el.removeAttribute(attr.name);
                    }
                });

                // 주의: <style> 태그는 제거하지만, 인라인 style="..." 속성은 보존
                // 이유: 인라인 스타일은 개별 요소에만 영향을 주므로 안전함
            });

            // 3. 정리된 HTML 반환
            return doc.body.innerHTML;
        }

        const eventListeners = {
            initialized: function (e: any) {
                var editor = e.editor

                loadedEditorIds.push(editor.ID as string)
                callbacks[editorId](editor)

                // 양식 초기화 시 원본 스타일/스크립트 추출 (보호 대상)
                let originalStylesMap = new Map<string, string>();
                let originalScriptsMap = new Map<string, string>();

                // 에디터 완전 로딩 후 원본 추출
                const extractOriginal = () => {
                    try {
                        const initialHtml = editor.getPublishingHtml() || '';
                        const parser = new DOMParser();
                        const doc = parser.parseFromString(initialHtml, 'text/html');

                        // 양식의 원본 <style> 태그 저장 (내용을 키로 사용해 중복 방지)
                        doc.querySelectorAll('style').forEach(styleEl => {
                            const content = styleEl.textContent || '';
                            if (content.trim()) {
                                originalStylesMap.set(content, styleEl.outerHTML);
                            }
                        });

                        // 양식의 원본 <script> 태그 저장
                        doc.querySelectorAll('script').forEach(scriptEl => {
                            const content = scriptEl.textContent || '';
                            if (content.trim()) {
                                originalScriptsMap.set(content, scriptEl.outerHTML);
                            }
                        });

                    } catch (e) {
                        console.error('Failed to extract original styles/scripts', e);
                    }
                };

                // 초기 추출 실행
                setTimeout(extractOriginal, 300);

                if(onedit) {
                    editor.setEventListener('afterEdit', (event: any) => {
                        var content = editor.getPublishingHtml();
                        onedit(content, editor);
                    })
                }

                // 붙여넣기 후처리: 위험 요소 제거 + 원본 스타일 복원
                editor.setEventListener('afterPaste', (event: any) => {
                    
                    // SynapEditor 내부 DOM 업데이트를 위한 최소 딜레이
                    setTimeout(() => {
                        try {
                            // 에디터 내부 document 접근 시도
                            let editorDoc: Document | null = null;
                            
                            // 방법 1: editorId로 에디터 컨테이너 찾아서 iframe 접근
                            const editorContainer = document.getElementById(editorId);
                            if (editorContainer) {
                                const iframe = editorContainer.querySelector('iframe') as HTMLIFrameElement;
                                if (iframe && iframe.contentDocument) {
                                    editorDoc = iframe.contentDocument;
                                }
                            }
                            
                            // 방법 2: .eum-editor 내부에서 찾기 (fallback)
                            if (!editorDoc) {
                                const editorWrappers = document.querySelectorAll('.eum-editor');
                                for (let i = 0; i < editorWrappers.length; i++) {
                                    const wrapper = editorWrappers[i];
                                    const parent = wrapper.closest(`#${editorId}`);
                                    if (parent || wrapper.id === editorId || wrapper.getAttribute('for') === editorId) {
                                        const iframe = wrapper.querySelector('iframe') as HTMLIFrameElement;
                                        if (iframe && iframe.contentDocument) {
                                            editorDoc = iframe.contentDocument;
                                            break;
                                        }
                                    }
                                }
                            }

                            if (!editorDoc) {
                                console.warn('Cannot access editor document for:', editorId);
                                return;
                            }

                            // 붙여넣기로 새로 들어온 위험한 요소만 제거
                            const dangerousTags = ['script', 'link', 'base', 'meta', 'iframe', 'object', 'embed'];
                            let removedCount = 0;
                            
                            dangerousTags.forEach(tag => {
                                const elements = editorDoc!.querySelectorAll(tag);
                                elements.forEach((el: Element) => {
                                    // 원본에 있던 요소가 아니면 제거
                                    const content = el.textContent || '';
                                    if (tag === 'script' && originalScriptsMap.has(content)) {
                                        return; // 원본은 유지
                                    }
                                    el.remove();
                                    removedCount++;
                                });
                            });

                            // 붙여넣기로 들어온 <style> 태그만 제거
                            const styleTags = editorDoc.querySelectorAll('style');
                            styleTags.forEach((styleEl: Element) => {
                                const content = styleEl.textContent || '';
                                if (!originalStylesMap.has(content)) {
                                    styleEl.remove();
                                    removedCount++;
                                }
                            });

                            // 이벤트 핸들러 속성 제거
                            const allElements = editorDoc.querySelectorAll('*');
                            allElements.forEach((el: Element) => {
                                const attrs = Array.from(el.attributes);
                                attrs.forEach((attr: Attr) => {
                                    if (attr.name.startsWith('on')) {
                                        el.removeAttribute(attr.name);
                                    }
                                    if (attr.value && typeof attr.value === 'string' &&
                                        attr.value.trim().toLowerCase().startsWith('javascript:')) {
                                        el.removeAttribute(attr.name);
                                    }
                                });
                            });

                            if (removedCount > 0) {
                                console.log(`Removed ${removedCount} dangerous elements from paste in editor:`, editorId);
                            }

                        } catch (error) {
                            console.error('Error in afterPaste for editor:', editorId, error);
                        }
                    }, 0); // 0ms: 현재 실행 스택 완료 후 즉시 실행
                });
            },
            initializedSync: function (e: any) {
                var editor = e.editor
            },
        }
      
        var editor = new SynapEditor(
            editorId,
            synapEditorConfig,
            initialValue,
            eventListeners
        );
        
        // 읽기모드인 경우 preview 모드로 변경
        if (mode === 'view') {
            editor.setMode('preview');
        }
    },
}
export default editorDirective
